import { useMemo } from 'react';
import { useLoaderData } from 'react-router';

import { cn } from '@rocco/utils/cn';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { CONTENT_CAP_PADDED } from '~/ui-rocco/pages/detail-shared';
import { SectionHead } from '~/ui-rocco/pages/detail-shared/SectionHead';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';

/** Five across, as the browse grids and the project's closing inventories run. */
const GRID_CLASS =
    'grid grid-cols-2 max-md:gap-x-3.5 max-md:gap-y-8 md:grid-cols-5 md:gap-x-3 md:gap-y-[6.25rem]';

/**
 * The professionals who install this product, as a page section: the
 * ordinary professional tiles, five to a row on the page's own ground, so
 * they read here exactly as they do on a browse grid.
 */
export const ProductInstallersSection = ({
    runIn,
}: {
    /**
     * The gap to whatever sits above, as Tailwind padding classes. Owned by
     * this section rather than left as a spacer on the page: this section is
     * absent on many products, and a page-level spacer then stacked against
     * the next one and doubled that section's run-in.
     */
    runIn: string;
}) => {
    const { product } = useLoaderData<typeof loader>();

    // The loader's own array, not a defaulted copy: `?? []` builds a new one
    // every render, which the memo below would then never hold.
    const professionals = product.Installers?.professionals;
    const tiles: TileViewModel<ProfessionalTileViewModel>[] = useMemo(
        () =>
            (professionals ?? [])
                .filter((professional) => professional.image?.src)
                .map((professional) => ({
                    model: professional,
                    aspectRatio: 'professional' as const,
                    section: 'ProductInstallers',
                    // What they do and where they are is a browse grid's job;
                    // under a product, the practice's name is the point.
                    hideServiceTags: true,
                    hideLocation: true,
                })),
        [professionals],
    );
    const withAdmin = useTilesAdminData(tiles);

    if (withAdmin.length === 0) return null;

    return (
        <section className={cn('bg-ground max-md:px-6 md:px-[3rem]', runIn)}>
            <div className={CONTENT_CAP_PADDED}>
                <SectionHead lead="Installers for" subject="this Product" />

                <div className={cn(GRID_CLASS, 'max-md:mt-8 md:mt-[4rem]')}>
                    {withAdmin.map((tile) => (
                        <CategoryPageTileWithAdminActions
                            key={`ProductInstallers-${tile.model.id}`}
                            tile={tile}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};
